import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/index.css";

import api from "@/api/client";
import { useAuthStore } from "@/store/auth";

// Auth pages
import LoginPage from "@/pages/admin/LoginPage";
import RegisterPage from "@/pages/admin/RegisterPage";
import ForgotPasswordPage from "@/pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/admin/ResetPasswordPage";

// Admin pages
import AdminLayout from "@/layouts/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import SpacesListPage from "@/pages/admin/SpacesListPage";
import SpaceEditorPage from "@/pages/admin/SpaceEditorPage";
import ArticleEditorPage from "@/pages/admin/ArticleEditorPage";
import OrgSettingsPage from "@/pages/admin/OrgSettingsPage";

// Public pages
import PublicLayout from "@/layouts/PublicLayout";
import PublicHomePage from "@/pages/public/PublicHomePage";
import PublicCategoryPage from "@/pages/public/PublicCategoryPage";
import PublicArticlePage from "@/pages/public/PublicArticlePage";
import PublicSearchPage from "@/pages/public/PublicSearchPage";

function getPlatformHostname() {
  const explicit = (process.env.REACT_APP_PLATFORM_HOST || "").trim().toLowerCase();
  if (explicit) return explicit;
  try {
    const raw = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return new URL(raw).hostname.toLowerCase();
  } catch {
    return window.location.hostname.toLowerCase();
  }
}

function ProtectedRoute({ children }) {
  const user = useAuthStore((s) => s.user);
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RootRedirect() {
  const user = useAuthStore((s) => s.user);
  if (user === undefined) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function DomainNotConfiguredPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-black tracking-tight mb-3">Domain not configured</h1>
        <p className="text-zinc-600 text-sm leading-relaxed">
          This hostname is not linked to a published documentation space yet. Add the custom domain in
          Handle Docs admin and verify DNS, or check that the space is published.
        </p>
      </div>
    </div>
  );
}

function PublicPortalRoutes({ customDomain = false }) {
  return (
    <Route path={customDomain ? "/" : "/s/:slug"} element={<PublicLayout customDomain={customDomain} />}>
      <Route index element={<PublicHomePage />} />
      <Route path="search" element={<PublicSearchPage />} />
      <Route path="c/:categorySlug" element={<PublicCategoryPage />} />
      <Route path="a/:articleSlug" element={<PublicArticlePage />} />
    </Route>
  );
}

function PlatformApp() {
  const loadMe = useAuthStore((s) => s.loadMe);
  useEffect(() => {
    loadMe();
  }, [loadMe]);

  return (
    <Routes>
      {PublicPortalRoutes({ customDomain: false })}

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/spaces" element={<SpacesListPage />} />
        <Route path="/spaces/:spaceId/*" element={<SpaceEditorPage />} />
        <Route path="/spaces/:spaceId/articles/:articleId" element={<ArticleEditorPage />} />
        <Route path="/settings" element={<OrgSettingsPage />} />
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function CustomDomainApp() {
  return (
    <Routes>
      {PublicPortalRoutes({ customDomain: true })}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [hostMode, setHostMode] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    const resolveHostMode = async () => {
      try {
        const host = window.location.hostname.toLowerCase();
        const localHosts = new Set(["localhost", "127.0.0.1", "::1"]);
        if (localHosts.has(host)) {
          if (!cancelled) setHostMode("platform");
          return;
        }

        const platformHost = getPlatformHostname();
        if (host === platformHost) {
          if (!cancelled) setHostMode("platform");
          return;
        }

        const res = await api.get("/public/host-check", {
          params: { domain: host },
          validateStatus: (s) => s === 200 || s === 403,
        });

        if (cancelled) return;

        if (res.status === 200) {
          const source = res.data?.source;
          if (source === "verified_custom_domain") setHostMode("custom");
          else if (source === "platform") setHostMode("platform");
          else setHostMode("blocked");
        } else {
          setHostMode("blocked");
        }
      } catch {
        if (!cancelled) setHostMode("blocked");
      }
    };

    resolveHostMode();
    return () => {
      cancelled = true;
    };
  }, []);

  if (hostMode === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading…
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster richColors closeButton position="top-right" />
      {hostMode === "blocked" && <DomainNotConfiguredPage />}
      {hostMode === "custom" && <CustomDomainApp />}
      {hostMode === "platform" && <PlatformApp />}
    </BrowserRouter>
  );
}

export default App;
